import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { StreamingText, StreamingLoader } from "@/components/StreamingText";
import { 
  Trophy,
  CheckCircle2,
  XCircle,
  Sparkles,
  RefreshCcw,
  BarChart3,
  Clock,
  Loader2,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const results = location.state?.results;
  
  const [aiExplanations, setAiExplanations] = useState({});
  const [loadingExplanation, setLoadingExplanation] = useState({});

  if (!results) {
    navigate("/quizzes");
    return null;
  }

  const { score, total_questions, percentage, time_taken, answers, quiz_title, category } = results;

  const getScoreColor = () => {
    if (percentage >= 90) return "text-emerald-500";
    if (percentage >= 75) return "text-blue-500";
    if (percentage >= 60) return "text-amber-500";
    return "text-rose-500";
  };

  const getScoreMessage = () => {
    if (percentage >= 90) return "Excellent! You're mastering this topic!";
    if (percentage >= 75) return "Great job! You're on the right track!";
    if (percentage >= 60) return "Good effort! Keep practicing!";
    return "Keep studying! You'll improve with practice.";
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getAIExplanation = async (answer, index) => {
    if (aiExplanations[index] || loadingExplanation[index]) return;
    
    setLoadingExplanation(prev => ({ ...prev, [index]: true }));
    
    // Initialize with empty string for streaming
    setAiExplanations(prev => ({
      ...prev,
      [index]: ""
    }));
    
    try {
      const response = await fetch(`${API}/explain-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: answer.question_text,
          selected_answer: answer.selected_answer || "No answer selected",
          correct_answer: answer.correct_answer,
          is_correct: answer.is_correct,
          all_options: answer.all_options || []
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch explanation');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = "";
      
      // Stream the response token by token
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        streamedText += chunk;
        
        // Update explanation in real-time
        setAiExplanations(prev => ({
          ...prev,
          [index]: streamedText
        }));
      }
    } catch (error) {
      toast.error("Failed to get AI explanation");
      console.error(error);
      // Remove the empty explanation on error
      setAiExplanations(prev => {
        const newExplanations = { ...prev };
        delete newExplanations[index];
        return newExplanations;
      });
    } finally {
      setLoadingExplanation(prev => ({ ...prev, [index]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="font-semibold" data-testid="results-title">Quiz Results</h1>
              <p className="text-sm text-slate-500">{quiz_title}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 mb-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-20 h-20 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-6"
          >
            <Trophy className={`w-10 h-10 ${getScoreColor()}`} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className={`text-6xl font-bold mb-2 ${getScoreColor()}`} data-testid="score-percentage">
              {percentage}%
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-6" data-testid="score-message">
              {getScoreMessage()}
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-2xl font-bold" data-testid="correct-count">{score}</span>
              </div>
              <p className="text-sm text-slate-500">Correct</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <div className="flex items-center justify-center gap-2 mb-1">
                <XCircle className="w-5 h-5 text-rose-500" />
                <span className="text-2xl font-bold" data-testid="incorrect-count">{total_questions - score}</span>
              </div>
              <p className="text-sm text-slate-500">Incorrect</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Clock className="w-5 h-5 text-violet-500" />
                <span className="text-2xl font-bold" data-testid="time-taken">{formatTime(time_taken)}</span>
              </div>
              <p className="text-sm text-slate-500">Time</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate("/quizzes")}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="try-another-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Try Another Quiz
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/analytics")}
              data-testid="view-analytics-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              View Analytics
            </Button>
          </div>
        </motion.div>

        {/* Questions Review */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-xl font-semibold mb-4" data-testid="review-title">Question Review</h3>
          
          <Accordion type="single" collapsible className="space-y-4">
            {answers.map((answer, index) => (
              <AccordionItem 
                key={index} 
                value={`question-${index}`}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
                data-testid={`review-question-${index}`}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-4 text-left">
                    {answer.is_correct ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-6 h-6 text-rose-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium line-clamp-2">{answer.question_text}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className={`p-4 rounded-xl ${answer.is_correct ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-rose-50 dark:bg-rose-900/20'}`}>
                        <p className="text-sm text-slate-500 mb-1">Your Answer</p>
                        <p className={`font-medium ${answer.is_correct ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                          {answer.selected_answer || "No answer selected"}
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
                        <p className="text-sm text-slate-500 mb-1">Correct Answer</p>
                        <p className="font-medium text-emerald-700 dark:text-emerald-400">
                          {answer.correct_answer}
                        </p>
                      </div>
                    </div>

                    {/* Static explanation */}
                    {answer.explanation && (
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <p className="text-sm font-semibold text-slate-600 mb-1">Quick Explanation</p>
                        <p className="text-slate-800 dark:text-slate-300">{answer.explanation}</p>
                      </div>
                    )}

                    {/* AI Explanation */}
                    {aiExplanations[index] !== undefined ? (
                      <div className="p-5 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-5 h-5 text-teal-600" />
                          <p className="text-base font-bold text-teal-700 dark:text-teal-400">Detailed AI Explanation</p>
                        </div>
                        {aiExplanations[index] === "" && loadingExplanation[index] ? (
                          <StreamingLoader message="Generating detailed explanation..." />
                        ) : (
                          <StreamingText 
                            text={aiExplanations[index]}
                            isStreaming={loadingExplanation[index]}
                          />
                        )}
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full bg-teal-50 hover:bg-teal-100 border-teal-300 text-teal-700 font-semibold"
                        onClick={() => getAIExplanation(answer, index)}
                        disabled={loadingExplanation[index]}
                        data-testid={`ai-explain-btn-${index}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get Detailed AI Explanation (Why Each Answer is Right/Wrong)
                      </Button>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </main>
    </div>
  );
}
